// Iter64 — Restricted Master Admin Access Code redemption page.
// Used by the two approved emails to restore their own master_admin role
// when their account was demoted to customer. Any other email is rejected
// with a clear restriction message.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, KeyRound, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function MasterAdminRestore() {
  const { refreshMe } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/master-admin-restore", { code });
      setSuccess(true);
      // Refresh the in-memory user so the AuthContext picks up the new role
      if (typeof refreshMe === "function") {
        await refreshMe();
      }
      setTimeout(() => navigate("/admin", { replace: true }), 1200);
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-12" data-testid="master-admin-restore-page">
      <div className="glass-panel p-6 sm:p-8 border-amber-400/30">
        <header className="mb-5 flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-amber-400/15 border border-amber-400/40 grid place-items-center shrink-0">
            <Shield className="text-amber-300" size={18} />
          </div>
          <div>
            <div className="eyebrow !text-amber-300">Restricted access</div>
            <h1 className="font-anton text-2xl uppercase text-white mt-1">
              Restore Master Admin
            </h1>
            <p className="text-xs text-lifted-dim mt-1">
              Enter your Master Admin access code. This code is restricted to
              two approved accounts; any other email will be rejected.
            </p>
          </div>
        </header>

        {success ? (
          <div
            className="rounded-md border border-lifted-green/40 bg-lifted-green/10 text-lifted-green p-3 text-sm flex items-center gap-2"
            data-testid="master-admin-restore-success"
          >
            <CheckCircle2 size={14} />
            Master Admin permissions restored. Redirecting…
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-lifted-dim mb-1">
                Access code
              </label>
              <div className="relative">
                <KeyRound
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-lifted-dim"
                  size={14}
                />
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  autoFocus
                  spellCheck={false}
                  autoComplete="off"
                  required
                  placeholder="MasterAdmin…"
                  className="w-full pl-9 pr-3 py-3 rounded-xl bg-black/40 border border-white/15 text-white placeholder:text-white/30 focus:outline-none focus:border-amber-400 font-mono"
                  data-testid="master-admin-code-input"
                />
              </div>
            </div>

            {err && (
              <div
                className="rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-200 text-sm px-3 py-2 flex items-start gap-2"
                data-testid="master-admin-restore-error"
              >
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <span>{err}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={busy || !code}
              className="btn-lifted w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="master-admin-restore-submit"
            >
              {busy ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Verifying…
                </>
              ) : (
                <>Restore Master Admin</>
              )}
            </button>
          </form>
        )}
      </div>

      <p className="text-[11px] text-lifted-dim mt-4 text-center">
        Attempted access is audit-logged. Unauthorized use will be reported.
      </p>
    </div>
  );
}
